import React, {useMemo, useState, useEffect} from 'react'
import {MC_2_0_PLANS} from '../../../../../../utils/constants'
import {THRYV_BASIC_CODES} from '../../../../../../utils/tgp-catalog-flags'
import Modal from '../../../../../../accountManagement/shared/Modal'
import {useDispatch, useSelector} from 'react-redux'
import {
  resetProposalInfo,
  selectProposalInfo,
  setProposalInfo,
  updateOption,
  updateProposalInfo,
} from '../../../../../../app-store/ProposalSlice'
import {
  ToggleTab,
  ToggleWrapper,
} from '../../../../../../accountManagement/shared/Tabs/Tabs.styles'
import {theme} from '../../../../../../accountManagement/theme'
import {
  AppsAddIcon,
  EmailIcon,
  InfoIcon,
} from '../../../../../../accountManagement/shared/icons'
import {DynamicField} from '../../../../../../accountManagement/features/AccountInfo/components/DynamicField'
import {buildFieldConfigMap} from '../../../../../../accountManagement/features/AccountInfo/config/locationFieldRules'
import {
  validateMobileNumber,
  validatePhoneNumber,
} from '../../../../../../utils/address-validation-util'
import {Button} from '../../../../../../accountManagement/shared/Button'
import {useNavigate} from 'react-router'
import {FooterRow} from '../../../../../../accountManagement/features/AddAccountModal/AddAccountModal.styles'
import {PanelLeft} from '../../../../../../features/CatalogGrid/components/ProductPlanSelector/ProductPlanSelector.styles'
import {ProposalCardTop} from '../../../../components/ProposalCardTop'
import {ProposalSentSuccessGif, PTCaretDownSVG} from '../../../../../../assets'
import {
  NotesSection,
  NotesToggle,
  NotesOptionalTag,
  NotesCaret,
  NotesBody,
  NotesTextarea,
  NotesFootRow,
  SendErrorBanner,
} from './styled.components'
import {useAuthClient} from '../../../../../../utils/use-auth-client'
import {useProposalPaymentPreview} from '../../hooks/useProposalPaymentPreview'
import {sanitizeOptions} from '../../hooks/useProposalSave'
import {getMonthlyRecurringTotal} from '../../../../utils/proposalTotals'
const TOGGLE_OPTIONS = [
  {label: 'Send via Email', id: 'email', icon: <EmailIcon />},
  {label: 'Send via Text Messages', id: 'sms', icon: <AppsAddIcon />},
]
const FIELD_CONFIG = [
  {
    key: 'clientPhone',
    label: 'Mobile Number',
    placeholder: '(555) 987-6543',
    type: 'tel',
    layout: 'full',
    disable: true,
    maxLength: 18,
    showFor: ['sms'],
    // 'mobile' on top of 'phone': the proposal link goes out as an SMS, so a
    // valid landline is not good enough.
    validators: ['required', 'phone', 'mobile'],
  },
  {
    key: 'clientEmail',
    label: 'Email Address',
    placeholder: 'john@acme.com',
    type: 'email',
    layout: 'full',
    disabled: true,
    showFor: ['email'],
    validators: ['required', 'email'],
  },
]

// Matches the repNotes cap in the service's proposal update schema.
const REP_NOTES_MAX = 2000

function runFieldValidators(label, value, fieldValidators, countryCode = 'US') {
  const trimmedValue = typeof value === 'string' ? value.trim() : value

  // REQUIRED
  if (fieldValidators.includes('required')) {
    const isEmpty =
      trimmedValue === undefined || trimmedValue === null || trimmedValue === ''
    if (isEmpty) return 'Required'
  }

  // EMAIL
  if (fieldValidators.includes('email') && trimmedValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmedValue)) return 'Invalid email address'
  }

  // PHONE — country-aware, same as formHelpers.js
  if (fieldValidators.includes('phone') && trimmedValue) {
    if (!validatePhoneNumber(countryCode, trimmedValue))
      return 'Invalid phone number'
  }

  // MOBILE — must be able to receive a text, not just be a valid number
  if (fieldValidators.includes('mobile') && trimmedValue) {
    if (!validateMobileNumber(countryCode, trimmedValue))
      return 'Enter a mobile number — this number cannot receive text messages'
  }

  return null
}

export const SendProposalModal = ({
  isSendProposalModalOpen,
  handleSendProposalModalStatus,
}) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('email')
  const proposalInfo = useSelector(selectProposalInfo)
  const selectedCountry = proposalInfo.country ?? 'US'
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [fieldValue, setFieldValue] = useState({
    clientEmail: proposalInfo.clientEmail,
    clientPhone: proposalInfo.clientPhone,
  })

  // Recalculate per-item dueTodayAmount whenever the modal opens so the
  // option cards reflect coupon discounts even if coupons were added after
  // the last save (which is when refreshPaymentPreview normally runs).
  useEffect(() => {
    if (isSendProposalModalOpen) {
      setIsPreviewLoading(true)
      refreshPaymentPreview().finally(() => setIsPreviewLoading(false))
      // Expand the note box straight away when one was already written, so an
      // existing note is never hidden behind a collapsed header.
      if (proposalInfo.repNotes) setIsNotesOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendProposalModalOpen])

  // Optional note from the rep, shown to the client on the proposal screen.
  const [isNotesOpen, setIsNotesOpen] = useState(false)
  const repNotes = proposalInfo.repNotes ?? ''

  // Set when the proposal link fails to go out, so the failure is visible
  // instead of the modal silently sitting there.
  const [sendError, setSendError] = useState(null)
  const [isSendProposalSent, setSendProposalStatus] = useState(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const dispatch = useDispatch()
  const client = useAuthClient()
  const {refreshPaymentPreview} = useProposalPaymentPreview()

  const fieldConfigMap = useMemo(
    () => buildFieldConfigMap({account: proposalInfo, values: proposalInfo}),
    [proposalInfo],
  )
  const [optionSelectStatusList, setOptionSelectStatusList] = useState(
    (proposalInfo.options || []).map(o => o.selected ?? false),
  )
  const resolvedFields = useMemo(
    () =>
      FIELD_CONFIG.map(f => ({
        ...f,
        ...(fieldConfigMap[f.key] || {}),
      })).filter(f => f.visible !== false),
    [fieldConfigMap],
  )

  const resetProposalModal = () => {
    setErrors({})
    setTouched({})
    setSendError(null)
    handleSendProposalModalStatus()
    setOptionSelectStatusList(optionSelectStatusList.map(item => false))
  }

  const checkSendProposalStatus = () => {
    // Validators come from FIELD_CONFIG rather than being repeated here, so the
    // Send gate can't drift from what the field itself validates.
    const activeField = FIELD_CONFIG.find(
      f => f.showFor?.includes(activeTab) ?? true,
    )
    if (activeField) {
      const fieldError = runFieldValidators(
        activeField.label,
        fieldValue[activeField.key],
        activeField.validators ?? [],
        selectedCountry ?? 'US',
      )
      if (fieldError) return true
    }
    if (!optionSelectStatusList.some(item => item)) {
      return true
    }
    return false
  }

  const renderSendProposalModalContent = () => {
    const handleChange = (fieldKey, value) => {
      const field = FIELD_CONFIG.find(f => f.key === fieldKey)
      if (!field) return

      // Editing the recipient makes a previous delivery failure stale.
      setSendError(null)

      const error = runFieldValidators(
        field.label,
        value,
        field.validators ?? [],
        selectedCountry ?? 'US',
      )
      setErrors(prev => {
        const next = {...prev}
        if (error) {
          next[fieldKey] = error
        } else {
          delete next[fieldKey]
        }
        return next
      })
      setFieldValue(prev => {
        const next = {...prev}
        next[fieldKey] = value
        return next
      })
      if (fieldKey === 'clientPhone' && !error) {
        dispatch(updateProposalInfo({clientPhone: value}))
      }
    }

    // Mirrors AccountInfo validateParentField: mark touched + validate on blur
    const handleBlur = fieldKey => {
      setTouched(prev => ({...prev, [fieldKey]: true}))

      const field = FIELD_CONFIG.find(f => f.key === fieldKey)
      if (!field) return

      const error = runFieldValidators(
        field.label,
        fieldValue[fieldKey],
        field.validators ?? [],
        selectedCountry ?? 'US',
      )
      setErrors(prev => {
        const next = {...prev}
        if (error) {
          next[fieldKey] = error
        } else {
          delete next[fieldKey]
        }
        return next
      })
    }

    const handleSelectProposalOption = (event, optionIndex) => {
      const option = proposalInfo.options[optionIndex]
      const newSelected = !optionSelectStatusList[optionIndex]

      setOptionSelectStatusList(prev => {
        const next = [...prev]
        next[optionIndex] = newSelected
        return next
      })

      dispatch(
        updateOption({
          displayOrder: option.displayOrder,
          changes: {isSent: newSelected},
          skipDirty: true,
        }),
      )
    }

    return (
      <div className="d-flex flex-column align-items-center w-full gap-4">
        <strong>Select below Options to Share with Client</strong>
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            justifyItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          {isPreviewLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.7)',
                zIndex: 1,
                borderRadius: '8px',
              }}
            >
              <style>{`@keyframes proposal-spin { to { transform: rotate(360deg); } }`}</style>
              <span
                style={{
                  display: 'inline-block',
                  width: '24px',
                  height: '24px',
                  border: '3px solid #dee2e6',
                  borderTopColor: '#6c757d',
                  borderRadius: '50%',
                  animation: 'proposal-spin 0.7s linear infinite',
                }}
              />
            </div>
          )}
          {(proposalInfo.options || [])
            .map((option, idx) => ({option, idx}))
            .filter(({option}) => option.items?.length > 0)
            .map(({option, idx}) => {
              const starCount = idx + 1
              const ALL_MC_CODES = new Set([
                'mc_plus-5-thryv-m2m',
                'mc_pro-5-thryv-m2m',
                'mc_pro_enh-5-thryv-m2m',
                'mc_foundation-5-thryv-m2m',
                ...THRYV_BASIC_CODES,
                ...MC_2_0_PLANS,
              ])
              const newItemCodes = new Set(
                (option.items || [])
                  .filter(i => !i.alreadyIncluded)
                  .map(i => i.productCode),
              )
              const hasNewMCPlan = [...newItemCodes].some(c =>
                ALL_MC_CODES.has(c),
              )
              const isDisplaced = code =>
                hasNewMCPlan &&
                ALL_MC_CODES.has(code) &&
                !newItemCodes.has(code)
              // FULSAS-14570: monthly recurring charges only (no one-time
              // fees, no annual plans) — see getMonthlyRecurringTotal.
              const effectiveMonthlyTotal = getMonthlyRecurringTotal(
                option.items || [],
                {isDisplaced, skipIncludedAddons: true},
              )
              const effectiveDueTodayTotal = (option.items || []).reduce(
                (sum, item) =>
                  sum +
                  Number(
                    item.dueTodayAmount ??
                      item.recurringAmount ??
                      item.price ??
                      item.unitPrice ??
                      0,
                  ) +
                  (item.addons || []).reduce(
                    (aSum, addon) =>
                      addon.alreadyIncluded
                        ? aSum
                        : aSum +
                          Number(addon.dueTodayAmount ?? addon.unitPrice ?? 0),
                    0,
                  ),
                0,
              )
              return (
                <ProposalCardTop
                  key={`proposal-option-${idx}`}
                  idx={proposalInfo.id || idx}
                  optionName={option.optionName ?? `proposal-${idx}`}
                  starCount={starCount}
                  dueTodayTotal={effectiveDueTodayTotal}
                  monthlyTotal={effectiveMonthlyTotal}
                  selectable={true}
                  selected={optionSelectStatusList[idx]}
                  onSelect={e => {
                    handleSelectProposalOption(e, idx)
                  }}
                  align="left"
                />
              )
            })}
        </div>
        <div className={'d-flex gap-2 align-items-center'}>
          <InfoIcon height={15} width={15} />
          <span>
            {(() => {
              const populated = (proposalInfo.options || [])
                .map((o, i) => ({o, i}))
                .filter(({o}) => o.items?.length > 0)
              const selectedCount = populated.filter(
                ({i}) => optionSelectStatusList[i],
              ).length
              return `${selectedCount} of ${populated.length} will be sent, at least 1 required`
            })()}
          </span>
        </div>
        <NotesSection>
          <NotesToggle
            type="button"
            aria-expanded={isNotesOpen}
            onClick={() => setIsNotesOpen(open => !open)}
          >
            Add details about this proposal
            <NotesOptionalTag>(optional)</NotesOptionalTag>
            <NotesCaret isOpen={isNotesOpen}>
              <PTCaretDownSVG width="10" height="10" fill="currentColor" />
            </NotesCaret>
          </NotesToggle>
          {isNotesOpen && (
            <NotesBody>
              <NotesTextarea
                value={repNotes}
                maxLength={REP_NOTES_MAX}
                placeholder="Anything you'd like the client to know about this proposal — what's included, why you recommend it, next steps."
                onChange={e =>
                  dispatch(updateProposalInfo({repNotes: e.target.value}))
                }
              />
              <NotesFootRow>
                <span>Shown to the client on their proposal page.</span>
                <span>
                  {repNotes.length}/{REP_NOTES_MAX}
                </span>
              </NotesFootRow>
            </NotesBody>
          )}
        </NotesSection>

        <ToggleWrapper
          variant="pill"
          style={{background: theme.colors.bg.blueSubtleBg}}
        >
          {TOGGLE_OPTIONS.map(option => (
            <ToggleTab
              key={option.id}
              isActive={option.id === activeTab}
              aria-selected={option.id === activeTab}
              variant="pill"
              onClick={() => {
                setActiveTab(option.id)
                setSendError(null)
                dispatch(updateProposalInfo({communicationMode: option.id}))
                // Clear values, errors, and touched for fields that belong
                // to the tab being left (i.e. fields with a showFor that
                // does not include the newly selected tab).
                const keysToReset = FIELD_CONFIG.filter(
                  f =>
                    f.showFor && !f.showFor.includes(option.id) && !f.disabled,
                ).map(f => f.key)
                if (keysToReset.length > 0) {
                  setFieldValue(prev => {
                    const next = {...prev}
                    keysToReset.forEach(k => {
                      next[k] = ''
                    })
                    return next
                  })
                  setErrors(prev => {
                    const next = {...prev}
                    keysToReset.forEach(k => delete next[k])
                    return next
                  })
                  setTouched(prev => {
                    const next = {...prev}
                    keysToReset.forEach(k => delete next[k])
                    return next
                  })
                }
              }}
            >
              {option.icon}
              {option.label}
            </ToggleTab>
          ))}
        </ToggleWrapper>

        <div className="d-flex w-full justify-content-center gap-4">
          {resolvedFields
            .filter(f => !f.showFor || f.showFor.includes(activeTab))
            .map(field => {
              const fieldElement = (
                <DynamicField
                  key={field.key}
                  field={field}
                  value={fieldValue[field.key] ?? ''}
                  values={proposalInfo}
                  error={errors[field.key]}
                  touched={touched[field.key]}
                  disabled={field.disabled}
                  labelLeftIcon={field.labelLeftIcon ?? ''}
                  infoText={field?.subTitle ?? ''}
                  variant={'form'}
                  size={'lg'}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  country={selectedCountry}
                  rightAction={field?.rightAction ?? false}
                  rightIcon={field?.rightIcon}
                />
              )

              if (field.layout === 'full') {
                return (
                  <div key={field.key} style={{width: '80%'}}>
                    {fieldElement}
                  </div>
                )
              }
              return (
                <div key={field.key} className="d-flex flex-column">
                  {fieldElement}
                </div>
              )
            })}
        </div>
        {sendError && (
          <SendErrorBanner role="alert">
            <InfoIcon height={15} width={15} />
            <span>{sendError}</span>
          </SendErrorBanner>
        )}

        <FooterRow>
          <Button
            variant="solid-gradiant"
            size="sm"
            leftIcon={<PanelLeft />}
            onClick={() => {
              resetProposalModal()
            }}
          >
            {'Back'}
          </Button>
          <Button
            variant="solid-gradiant"
            size="sm"
            disabled={checkSendProposalStatus() || isPreviewLoading}
            isLoading={isPreviewLoading}
            onClick={async () => {
              setIsPreviewLoading(true)
              setSendError(null)
              try {
                const latestProposalInfo = await refreshPaymentPreview()
                const communicationMode =
                  proposalInfo?.communicationMode ?? activeTab ?? 'email'
                await client(`proposals/${proposalInfo.proposalId}`, {
                  data: {
                    statusCode: 'SENT',
                    communicationMode,
                    repNotes: repNotes.trim() || null,
                    options: sanitizeOptions(
                      latestProposalInfo.options.map((option, idx) => ({
                        ...option,
                        isSent: optionSelectStatusList[idx] ?? false,
                      })),
                    ),
                  },
                  method: 'PUT',
                })
                const phoneNum =
                  activeTab === 'sms'
                    ? fieldValue.clientPhone || proposalInfo.clientPhone
                    : undefined
                await client(`proposals/${proposalInfo.proposalId}/send-link`, {
                  data: {
                    proposalLink: proposalInfo.proposalLink,
                    channel: communicationMode,
                    email: proposalInfo.clientEmail,
                    ...(phoneNum ? {phoneNum} : {}),
                    country: proposalInfo.country,
                  },
                })
                dispatch(
                  setProposalInfo({
                    ...latestProposalInfo,
                    communicationMode,
                    statusCode: 'SENT',
                  }),
                )
                setSendProposalStatus(true)
              } catch (err) {
                // Delivery failed (bad number, carrier rejection, provider
                // error). Stay on this screen with the reason so the rep can
                // fix the number or switch channel and retry.
                setSendError(
                  err?.message ||
                    (activeTab === 'sms'
                      ? "We couldn't send the text message. Check the number or send by email instead."
                      : "We couldn't send the email. Check the address and try again."),
                )
              } finally {
                setIsPreviewLoading(false)
              }
            }}
          >
            {'Send Proposal'}
          </Button>
        </FooterRow>
      </div>
    )
  }

  const renderSendProposalSuccessView = () => {
    return (
      <>
        <div className="d-flex flex-column align-items-center">
          <img
            src={ProposalSentSuccessGif}
            height={480}
            width={360}
            alt="Sent Proposal Success Gif"
          />
          <div className={'d-flex gap-2 align-items-center'}>
            <InfoIcon height={15} width={15} />
            <span>
              {`Proposal sent via ${activeTab === 'email' ? 'Email' : 'Phone'}`}
            </span>
          </div>
          <strong>
            {activeTab === 'email'
              ? proposalInfo.clientEmail
              : proposalInfo.clientPhone || fieldValue.clientPhone}
          </strong>
          <br />
          <FooterRow>
            <Button
              variant="solid-gradiant"
              size="sm"
              disabled={false}
              onClick={() => {
                dispatch(resetProposalInfo())
                handleSendProposalModalStatus()
                navigate('/proposal-tool', {state: {skipBlocker: true}})
              }}
            >
              {'Go Back To Proposal Dashboard'}
            </Button>
          </FooterRow>
        </div>
      </>
    )
  }

  return (
    <Modal
      isOpen={isSendProposalModalOpen}
      onClose={() => {
        resetProposalModal()
      }}
      showCloseButton={!isSendProposalSent}
      closeOnBackdrop={false}
      closeOnEsc={false}
      title={
        isSendProposalSent ? 'Proposal Sent Successfully!' : 'Send Proposal'
      }
      subtitle={`${isSendProposalSent ? 'Sent' : 'Send'} proposal options to ${
        proposalInfo.businessName ?? ''
      }`}
      size={isSendProposalSent ? 'md' : 'lg'}
      align={'center'}
    >
      <div className="flex-column">
        {isSendProposalSent
          ? renderSendProposalSuccessView()
          : renderSendProposalModalContent()}
      </div>
    </Modal>
  )
}
